import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Header from 'components/header';
import Sidebar from 'components/sidebar';
import TopBarComponent from 'components/ExploreTL/TopBar';
import { Box, Typography, CircularProgress } from '@mui/material';
import Content from 'components/ExploreTL/Content';
import nodatafoundImage from '../utilities/dummy_assets/nodatafound.jpg';

// Importing APIs
import MachineListAPIs from 'utilities/api/StudentMachineList';
import InventoryListAPIs from 'utilities/api/StudentInventoryList';
import EquipmentListAPIs from 'utilities/api/StudentEquipmentList';

const ExploreTL = () => {
  const { access, user } = useSelector((state) => state.user);
  const location = useLocation();

  const [activeCategory, setActiveCategory] = useState(0);
  const [activeView, setActiveView] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Paginated Data States
  const [machineData, setMachineData] = useState({ results: [], count: 0, page: 1 });
  const [equipmentData, setEquipmentData] = useState({ results: [], count: 0, page: 1 });
  const [inventoryData, setInventoryData] = useState({ results: [], count: 0, page: 1 });

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // New states for Inventory Filters
  const [selectedInventoryCategories, setSelectedInventoryCategories] = useState([]);
  const [selectedInventoryTags, setSelectedInventoryTags] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableTags, setAvailableTags] = useState(InventoryListAPIs.GetInventoryTags());

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      // Reset pages on new search
      setMachineData(prev => ({ ...prev, page: 1 }));
      setEquipmentData(prev => ({ ...prev, page: 1 }));
      setInventoryData(prev => ({ ...prev, page: 1 }));
    }, 500);
    return () => clearTimeout(handler);
  }, [search, selectedInventoryCategories, selectedInventoryTags]);

  useEffect(() => {
    const fetchMetadata = async () => {
        const categories = await InventoryListAPIs.GetInventoryCategories();
        setAvailableCategories(categories || []);
    };
    fetchMetadata();
  }, []);

  useEffect(() => {
    const fetchCategoryData = async () => {
      setLoading(true);
      try {
        if (activeCategory === 0) {
          const res = await MachineListAPIs.FilterMachineListGet({
            accessToken: access,
            page: machineData.page,
            search: debouncedSearch,
            page_size: 12
          });
          setMachineData(prev => ({ ...prev, results: res.results || [], count: res.count || 0 }));
        } else if (activeCategory === 1) {
          const res = await EquipmentListAPIs.FilterEquipmentListGet({
            accessToken: access,
            page: equipmentData.page,
            search: debouncedSearch,
            page_size: 12
          });
          setEquipmentData(prev => ({ ...prev, results: res.results || [], count: res.count || 0 }));
        } else if (activeCategory === 2) {
          const res = await InventoryListAPIs.FilterInventoryListGet({
            accessToken: access,
            page: inventoryData.page,
            search: debouncedSearch,
            category: selectedInventoryCategories.join(','),
            tag: selectedInventoryTags.join(','),
            page_size: 12
          });
          setInventoryData(prev => ({ ...prev, results: res.results || [], count: res.count || 0 }));
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    };

    fetchCategoryData();
  }, [access, activeCategory, debouncedSearch, machineData.page, equipmentData.page, inventoryData.page, selectedInventoryCategories, selectedInventoryTags]);

  useEffect(() => {
    if (location.state && location.state.activeCategory !== undefined) {
      setActiveCategory(location.state.activeCategory);
    }
  }, [location.state]);

  // UI Helper for Pagination Change
  const handlePageChange = (newPage) => {
    if (activeCategory === 0) setMachineData(prev => ({ ...prev, page: newPage }));
    else if (activeCategory === 1) setEquipmentData(prev => ({ ...prev, page: newPage }));
    else if (activeCategory === 2) setInventoryData(prev => ({ ...prev, page: newPage }));
  };

  // Set data to display based on active category
  let dataToDisplay = [];
  let totalCount = 0;
  let currentPage = 1;

  switch (activeCategory) {
    case 0:
      dataToDisplay = machineData.results;
      totalCount = machineData.count;
      currentPage = machineData.page;
      break;
    case 1:
      dataToDisplay = equipmentData.results;
      totalCount = equipmentData.count;
      currentPage = equipmentData.page;
      break;
    case 2:
      dataToDisplay = inventoryData.results;
      totalCount = inventoryData.count;
      currentPage = inventoryData.page;
      break;
    default:
      break;
  }

  const isLoading = initialLoading || loading; 
  const hasData = dataToDisplay && dataToDisplay.length > 0;
  
  const handleSearchChange = (val) => {
    setSearch(val);
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <Sidebar />
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
        }}
      >
        <Header />
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            margin: '0px 20px',
            position: 'relative',
            height: '100%',
          }}
        >
          <TopBarComponent
            activeCategory={activeCategory}
            setActiveCategory={setActiveCategory}
            setSearch={handleSearchChange}
            activeView={activeView}
            setActiveView={setActiveView}
          />
          {isLoading ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flexGrow={1}>
                {/* Premium Spinner */}
                <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress
                        variant="determinate"
                        sx={{ color: (theme) => theme.palette.grey[200] }}
                        size={60}
                        thickness={4}
                        value={100}
                    />
                    <CircularProgress
                        variant="indeterminate"
                        disableShrink
                        sx={{
                            color: '#1a237e',
                            animationDuration: '600ms',
                            position: 'absolute',
                            left: 0,
                            '& .MuiCircularProgress-circle': {
                                strokeLinecap: 'round',
                            },
                        }}
                        size={60}
                        thickness={4}
                    />
                </Box>
                <Typography variant="h6" sx={{ mt: 2, color: '#1a237e', fontWeight: 600 }}>
                    Fetching resources...
                </Typography>
            </Box>
          ) : (
            <Content
              data={dataToDisplay}
              searchQuery={search}
              activeCategory={activeCategory}
              activeView={activeView}
              setSearch={handleSearchChange}
              loading={isLoading}
              totalCount={totalCount}
              page={currentPage}
              onPageChange={handlePageChange}
              // Pass filter props to Content
              availableCategories={availableCategories}
              availableTags={availableTags}
              selectedInventoryCategories={selectedInventoryCategories}
              selectedInventoryTags={selectedInventoryTags}
              setSelectedInventoryCategories={setSelectedInventoryCategories}
              setSelectedInventoryTags={setSelectedInventoryTags}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ExploreTL;
